import { useState, useMemo, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { SearchableSelect } from '@/components/SearchableSelect'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  NONE,
  extractErr,
  RecordDrawer,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────
type Department = {
  id: number
  code: string
  name: string
  description: string
  parent: number | null
  parent_name: string | null
  manager: number | null
  manager_name: string | null
  is_billable_to_client: boolean
  is_active: boolean
  employee_count: number
}

// Minimal create payload — rest filled in on the record page.
type CreateInput = {
  name: string
  description: string
  parent: number | null
  is_billable_to_client: boolean
}

export default function HRDepartments() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { confirm, dialog } = useDeleteConfirm()

  const { data, isLoading } = useQuery({
    queryKey: ['hr', 'departments'],
    queryFn: async () => (await api.get<Department[]>('/hr/departments/')).data,
  })

  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<CreateInput>(emptyDraft())

  function openCreate() {
    setDraft(emptyDraft())
    setCreating(true)
  }

  const createMut = useMutation({
    mutationFn: async () => (await api.post<Department>('/hr/departments/', {
      ...draft,
      manager_person: null,
      is_active: true,
    })).data,
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['hr', 'departments'] })
      toast.success('Department created')
      setCreating(false)
      navigate(`/admin/hr/departments/${created.id}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/hr/departments/${id}/`),
    onSuccess: () => {
      toast.success('Department deleted')
      qc.invalidateQueries({ queryKey: ['hr', 'departments'] })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const departments = data || []

  // Depth-of-tree for indentation. Stops at 10 to guard against cycles.
  const depthOf = useMemo(() => {
    const byId: Record<number, Department> = {}
    departments.forEach((d) => (byId[d.id] = d))
    const out: Record<number, number> = {}
    departments.forEach((d) => {
      let depth = 0, cur = d
      while (cur.parent && byId[cur.parent]) {
        depth++
        cur = byId[cur.parent]
        if (depth > 10) break
      }
      out[d.id] = depth
    })
    return out
  }, [departments])

  // Render the tree top-down: top-level departments first, each followed by
  // its descendants in pre-order. Stable for arbitrary fetch order.
  const ordered = useMemo(() => {
    const childrenOf: Record<number, Department[]> = {}
    departments.forEach((d) => {
      const k = d.parent ?? 0
      ;(childrenOf[k] = childrenOf[k] || []).push(d)
    })
    Object.values(childrenOf).forEach((arr) => arr.sort((a, b) => a.code.localeCompare(b.code)))
    const out: Department[] = []
    function walk(parentId: number) {
      for (const d of childrenOf[parentId] || []) {
        out.push(d); walk(d.id)
      }
    }
    walk(0)
    return out
  }, [departments])

  return (
    <AdminLayout>
      {dialog}

      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Departments</h2>
          <p className="text-sm text-muted-foreground">{departments.length} total</p>
        </div>
        <Button onClick={openCreate}><Plus className="h-3 w-3 mr-1" /> New department</Button>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : ordered.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">No departments.</div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                <Th>Code</Th>
                <Th>Name</Th>
                <Th>Manager</Th>
                <Th className="text-right">Employees</Th>
                <Th>Billable</Th>
                <Th>Active</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {ordered.map((d) => (
                <tr
                  key={d.id}
                  className="hover:bg-muted/40 cursor-pointer"
                  onClick={() => navigate(`/admin/hr/departments/${d.id}`)}
                >
                  <Td className="font-mono text-xs">
                    <span style={{ paddingLeft: `${(depthOf[d.id] || 0) * 16}px` }}>{d.code}</span>
                  </Td>
                  <Td className="font-medium">{d.name}</Td>
                  <Td className="text-xs">{d.manager_name || <span className="text-muted-foreground">—</span>}</Td>
                  <Td className="text-right text-xs">{d.employee_count}</Td>
                  <Td className="text-xs">
                    {d.is_billable_to_client
                      ? <span className="text-blue-700">Billable</span>
                      : <span className="text-muted-foreground">Internal</span>}
                  </Td>
                  <Td className="text-xs">
                    <span className={`inline-block w-2 h-2 rounded-full ${d.is_active ? 'bg-green-500' : 'bg-muted-foreground'}`} />
                  </Td>
                  <Td className="text-right" onClick={(ev) => ev.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/admin/hr/departments/${d.id}`)}>
                          Open
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          disabled={d.employee_count > 0}
                          onClick={() => d.employee_count === 0 && confirm(
                            `department ${d.code} (${d.name})`,
                            () => deleteMut.mutate(d.id),
                          )}
                          className={d.employee_count === 0 ? 'text-destructive' : ''}
                        >
                          {d.employee_count === 0
                            ? 'Delete'
                            : `Delete (${d.employee_count} employees)`}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create drawer — minimal fields. Manager, hierarchy details on the record page. */}
      <RecordDrawer
        open={creating}
        onOpenChange={(v) => { if (!v) setCreating(false) }}
        title="New department"
        description="Create the department with the essentials. Manager and additional hierarchy details are filled in on the record page."
        onSubmit={() => createMut.mutate()}
        isPending={createMut.isPending}
        submitLabel="Create"
      >
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="dp-name">Name *</FieldLabel>
            <Input id="dp-name" autoFocus required value={draft.name}
              onChange={(ev) => setDraft({ ...draft, name: ev.target.value })} />
          </Field>

          <Field>
            <FieldLabel htmlFor="dp-parent">Parent department</FieldLabel>
            <SearchableSelect
              id="dp-parent"
              value={draft.parent ? String(draft.parent) : NONE}
              onChange={(v) => setDraft({ ...draft, parent: v === NONE ? null : Number(v) })}
              placeholder="— top-level —"
              options={[
                { value: NONE, label: '— top-level —' },
                ...ordered.filter((d) => d.is_active).map((d) => ({
                  value: String(d.id),
                  label: `${' '.repeat((depthOf[d.id] || 0) * 2)}${d.name}`,
                  hint: d.code,
                })),
              ]}
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="dp-desc">Description</FieldLabel>
            <Textarea id="dp-desc" rows={2} value={draft.description}
              onChange={(ev) => setDraft({ ...draft, description: ev.target.value })} />
          </Field>

          <Field>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={draft.is_billable_to_client}
                onChange={(ev) => setDraft({ ...draft, is_billable_to_client: ev.target.checked })} />
              Billable to client
            </label>
            <FieldDescription>
              Used by the Bench calculation: only employees in billable departments count as available bench.
            </FieldDescription>
          </Field>
        </FieldGroup>
      </RecordDrawer>
    </AdminLayout>
  )
}

function emptyDraft(): CreateInput {
  return { name: '', description: '', parent: null, is_billable_to_client: true }
}

function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-sm text-foreground ${className}`} onClick={onClick}>{children}</td>
}
